export const SETUP_SIGN_IN_MESSAGE = 'Your edits are safe. Please sign in to keep saving your invitation.'

let authenticating

// Keep the editing document (and its unsaved canvas/form state) mounted. The
// same-origin frame uses the existing Rails login/signup/password-reset forms.
// Confirm authentication with the server: an iframe load event alone can be
// missed (or its final redirect delayed), and must not strand a signed-in host.
export function signInForPartySetup(Swal) {
    if (authenticating) return authenticating
    authenticating = new Promise(resolve => {
        let closed = false, checking = false, pollTimer, loadingTimer, frame, status, retry
        const cleanup = () => {
            closed = true
            clearTimeout(pollTimer)
            clearTimeout(loadingTimer)
            frame?.removeEventListener('load', loaded)
            retry?.removeEventListener('click', loadSignIn)
        }
        const showRetry = message => {
            status.textContent = message
            status.hidden = false
            retry.hidden = false
        }
        const checkSession = async () => {
            if (closed || checking) return
            clearTimeout(pollTimer)
            checking = true
            try {
                if (document.visibilityState !== 'hidden') {
                    // This probe never writes a session cookie. A normal token
                    // refresh racing the login response could undo that login.
                    const response = await fetch('/party/setup-session?check=1', {
                        credentials: 'same-origin', cache: 'no-store', redirect: 'error',
                        signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(10000) : undefined,
                        headers: { Accept: 'application/json', 'X-ECHOage-Setup': '1' }
                    })
                    if (!response.ok) throw new Error('Session check unavailable')
                    const session = await response.json()
                    if (closed) return
                    if (session.authenticated === true) {
                        const refreshed = await freshSession()
                        if (!closed && refreshed.authenticated === true) {
                            cleanup()
                            resolve(true)
                            Swal.close()
                        }
                    }
                }
            } catch (_) {
                // No form is replayed and the editor remains mounted.
                if (!closed) showRetry('We couldn’t reconnect. Your invitation and details are still here. Please try again.')
            } finally {
                checking = false
                if (!closed) pollTimer = setTimeout(checkSession, 3000)
            }
        }
        const loaded = () => {
            if (closed) return
            try {
                const page = frame.contentDocument?.querySelector('.login-section')
                if (page) {
                    clearTimeout(loadingTimer)
                    status.hidden = true
                    retry.hidden = true
                    frame.style.height = `${Math.min(page.scrollHeight + 12, window.innerHeight * 0.75)}px`
                }
            } catch (_) { /* Never read a frame outside our origin. */ }
            void checkSession()
        }
        const loadSignIn = () => {
            if (closed) return
            clearTimeout(loadingTimer)
            status.textContent = 'Loading sign-in… Your invitation and details will stay on this page.'
            status.hidden = false
            retry.hidden = true
            loadingTimer = setTimeout(() => {
                if (!closed) showRetry('Sign-in is taking longer than expected. Retry without leaving your invitation.')
            }, 10000)
            frame.src = '/login?continue_to=%2Fparty%2Fsetup-authenticated'
            void checkSession()
        }
        Swal.fire({
            html: '<p data-setup-auth-status role="status" class="small mb-2"></p><button type="button" data-setup-auth-retry class="btn blue-button mb-3" hidden>Retry sign-in</button><iframe title="Sign in or create your ECHOage account" style="width:100%;height:min(660px,75vh);border:0;display:block"></iframe>',
            width: 540, padding: '1.5rem 0.5rem 0.5rem', showConfirmButton: false,
            showCloseButton: true, closeButtonAriaLabel: 'Close sign in and keep editing',
            allowOutsideClick: false,
            didOpen: popup => {
                frame = popup.querySelector('iframe')
                status = popup.querySelector('[data-setup-auth-status]')
                retry = popup.querySelector('[data-setup-auth-retry]')
                // Install listeners before starting navigation, including cached loads.
                frame.addEventListener('load', loaded)
                retry.addEventListener('click', loadSignIn)
                loadSignIn()
            },
            willClose: () => { cleanup(); resolve(false) },
            didDestroy: () => { cleanup(); resolve(false) }
        })
    }).finally(() => { authenticating = null })
    return authenticating
}

let refreshing

export async function freshSession() {
    if (!refreshing) {
        refreshing = fetch('/party/setup-session', {
            credentials: 'same-origin', cache: 'no-store', redirect: 'error',
            signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(10000) : undefined,
            headers: { Accept: 'application/json', 'X-ECHOage-Setup': '1' }
        }).then(async response => {
            if (!response.ok) throw new Error('Could not refresh your session. Keep this page open and try again.')
            const session = await response.json()
            if (!session.csrf_token) throw new Error('Could not refresh your session. Keep this page open and try again.')
            const meta = document.querySelector('meta[name="csrf-token"]')
            if (meta) meta.content = session.csrf_token
            return session
        }).finally(() => { refreshing = null })
    }
    return refreshing
}

// Only opt-in invitation writes use this helper. A rejected CSRF request has
// not performed the write, so its exact snapshot can safely be retried once.
// Never reload/navigate the editing tab or replay a network/validation failure.
export async function partySetupRequest(url, options, { requiresAccount = false } = {}) {
    const request = { ...options, credentials: 'same-origin' }
    let response = await fetch(url, request)
    if (response.status !== 422 || !response.clone) return response
    const failure = await response.clone().json().catch(() => null)
    if (failure?.code !== 'setup_csrf_expired') return response

    const session = await freshSession()
    if (requiresAccount && !session.authenticated) {
        return new Response(JSON.stringify({ authentication_required: true, error: SETUP_SIGN_IN_MESSAGE }), {
            status: 401, headers: { 'Content-Type': 'application/json' }
        })
    }
    return fetch(url, { ...request, headers: { ...request.headers, 'X-CSRF-Token': session.csrf_token } })
}
